/** Origin of the public KidsBooked site (the camp pages partners are shown). */
export const CAMP_PUBLIC_ORIGIN = "https://kidsbooked.com";

/**
 * Builds the public camp page URL from a location's `slugPath`.
 *
 * `campLocations.slugPath` is maintained server-side by `syncCampSlugPathsForOrg`
 * and holds only the path suffix — `/<orgSlug>` or `/<orgSlug>/<locationSlug>` —
 * with no `/camp` prefix. It is `""` when the location has no public page yet
 * (org has no slug, or the location is neither slugged nor the org's canonical one),
 * and in that case there is no URL to show: callers render a fallback rather than
 * a link that 404s.
 */
export function campPublicUrlFromSlugPath(slugPath?: string | null): string | null {
  const path = typeof slugPath === "string" ? slugPath.trim() : "";
  if (!path.startsWith("/")) return null;
  return `${CAMP_PUBLIC_ORIGIN}/camp${path}`;
}

/**
 * Three-state resolution of a location's public page, because "no URL" has two
 * different causes and only one of them is a fact we may state.
 *
 * `syncCampSlugPathsForOrg` writes `""` when it has computed that the location
 * has no public path — that is a verified absence. A doc it has never touched
 * carries no `slugPath` key at all, and that is not the same claim: the sync is
 * gated on a routing-relevant change (slug, org, workflowStatus), so an untouched
 * location may well have a page that simply has not been denormalized yet. The
 * canonical resolver `resolveCampSlugPathForOffer` recomputes from org + siblings
 * for exactly this reason.
 *
 * Telling a camp partner their location "has no page" — and to email support —
 * is load-bearing on what they do next, so the `unknown` arm must not say it.
 */
export type CampPublicPage =
  | { kind: "url"; url: string }
  | { kind: "none" }
  | { kind: "unknown" };

export function resolveCampPublicPage(slugPath?: string | null): CampPublicPage {
  if (typeof slugPath !== "string") return { kind: "unknown" };
  const url = campPublicUrlFromSlugPath(slugPath);
  if (url) return { kind: "url", url };
  return slugPath.trim() === "" ? { kind: "none" } : { kind: "unknown" };
}
